import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Contact = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [infoRef, infoVisible] = useScrollAnimation({ threshold: 0.1 });
  const [formRef, formVisible] = useScrollAnimation({ threshold: 0.1 });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Simulate form submission
    console.log('Form submitted:', formData);
    toast.success('Thank you! We\'ll be in touch shortly.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      message: ''
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      details: "+357 94 592 592",
      link: "tel:+35794592592"
    },
    {
      icon: Mail,
      title: "Email",
      details: "info@paglobalgroup.com",
      link: "mailto:info@paglobalgroup.com"
    },
    {
      icon: MapPin,
      title: "Office",
      details: "Larnaca, Cyprus",
      link: null
    }
  ];

  return (
    <section id="contact" className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div ref={titleRef} className={`text-center mb-16 scroll-animate ${titleVisible ? 'visible' : ''}`}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a3a5c] mb-4">
              Get In Touch
            </h2>
            <div className="w-24 h-1 bg-[#0075be] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ready to elevate your property consultancy experience? Contact our expert team today.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div ref={infoRef} className={`scroll-animate-slide-left ${infoVisible ? 'visible' : ''}`}>
              <h3 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                Let's Start a Conversation
              </h3>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Our team of property consultancy experts is here to support your success. Whether you need advice on a specific transaction or ongoing strategic guidance, we're ready to help.
              </p>

              {/* Contact Cards */}
              <div className="space-y-4 mb-8">
                {contactInfo.map((info, index) => (
                  <div 
                    key={index}
                    className="flex items-start space-x-4 p-5 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border-l-4 border-[#0075be]"
                  >
                    <div className="bg-[#1a3a5c] p-3 rounded-lg flex-shrink-0">
                      <info.icon className="text-white" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a3a5c] mb-1">{info.title}</p>
                      {info.link ? (
                        <a 
                          href={info.link}
                          className="text-gray-700 hover:text-[#0075be] transition-colors"
                        >
                          {info.details}
                        </a>
                      ) : (
                        <p className="text-gray-700">{info.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Office Hours */}
              <div className="bg-[#1a3a5c] text-white p-6 rounded-lg">
                <h4 className="font-bold text-xl mb-4">Office Hours</h4>
                <div className="space-y-2 text-gray-100">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 2:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div ref={formRef} className={`bg-white p-8 rounded-lg shadow-xl border border-gray-100 scroll-animate-slide-right ${formVisible ? 'visible' : ''}`}>
              <h3 className="text-2xl font-bold text-[#1a3a5c] mb-6">
                Send Us a Message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    required
                    className="w-full border-gray-300 focus:border-[#0075be] focus:ring-[#0075be]"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john.smith@agency.com"
                    required
                    className="w-full border-gray-300 focus:border-[#0075be] focus:ring-[#0075be]"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 7XXX XXXXXX"
                    className="w-full border-gray-300 focus:border-[#0075be] focus:ring-[#0075be]"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    Agency/Company
                  </label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Agency Name"
                    className="w-full border-gray-300 focus:border-[#0075be] focus:ring-[#0075be]"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your consultancy needs..."
                    rows={5}
                    required
                    className="w-full border-gray-300 focus:border-[#0075be] focus:ring-[#0075be] resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#0075be] hover:bg-[#1a3a5c] text-white text-lg py-6 transition-colors duration-200 shadow-lg"
                >
                  Send Message
                  <Send className="ml-2" size={20} />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
